import dayjs from 'dayjs';
import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { MetricName } from '@/swagger/constants/slash-enums';
import { API_GET_SUBSCRIPTION, KEY_GET_SUBSCRIPTION } from '../constants';
import { MOCK_RESPONSE_GET_SUBSCRIPTION } from '../mocks';
import {
  Subscription,
  SubscriptionInterval,
  SubscriptionPaymentMethod,
  SubscriptionPlanType,
} from '../types';

export type CallParamsGetSubscription = {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetSubscription = Subscription;

type CallErrorGetSubscription = GenericApiError;

type CallSelectorsGetSubscription = {
  subscription: Subscription | undefined;
  isActive: boolean;
  isTrialActive: boolean;
  trialDays: number;
  planNickname: string;
  planType: SubscriptionPlanType | undefined;
  learningLicenses: number;
  learningLicensesMax: number | undefined;
  adminLicenses: number;
  pricePerUserAmount: number;
  pricePerUserCurrency: string;
  totalAmount: number;
  totalCurrency: string;
  paymentInterval: SubscriptionInterval | undefined;
  startAt: string;
  paymentMethod: SubscriptionPaymentMethod | undefined;
  paymentNextDate: string;
};

const computeTrialDays = (sub: Subscription | undefined): number => {
  const startAt = sub?.trialData?.startAt;
  const endAt = sub?.trialData?.endAt;
  if (!startAt || !endAt) {
    return 0;
  }
  return Math.max(0, dayjs(endAt).diff(dayjs(startAt), 'days'));
};

const getMetricValue = (sub: Subscription | undefined, name: `${MetricName}`) =>
  sub?.usage?.metrics?.find(m => m.name === name)?.value ?? 0;

const getMetricMax = (sub: Subscription | undefined, name: `${MetricName}`) =>
  sub?.usage?.metrics?.find(m => m.name === name)?.max;

export const getSubscriptionCall = createGeneralCall<
  CallParamsGetSubscription,
  CallResponseGetSubscription,
  CallErrorGetSubscription,
  CallSelectorsGetSubscription
>({
  id: KEY_GET_SUBSCRIPTION,
  url: () => state => {
    const org = getCurrentOrganization(state);
    return API_GET_SUBSCRIPTION(org as unknown as string);
  },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () =>
      payload,
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_SUBSCRIPTION : undefined,
  selectors: {
    subscription: ({ selectors }) => createSelector(selectors.response, res => res),
    isActive: ({ selectors }) => createSelector(selectors.response, res => res?.active ?? false),
    isTrialActive: ({ selectors }) =>
      createSelector(selectors.response, res => res?.trialData?.active ?? false),
    trialDays: ({ selectors }) => createSelector(selectors.response, res => computeTrialDays(res)),
    planNickname: ({ selectors }) =>
      createSelector(selectors.response, res => res?.plan?.nickname ?? ''),
    planType: ({ selectors }) => createSelector(selectors.response, res => res?.plan?.type),
    learningLicenses: ({ selectors }) =>
      createSelector(selectors.response, res => getMetricValue(res, 'LEARNING_LICENSES')),
    learningLicensesMax: ({ selectors }) =>
      createSelector(selectors.response, res => getMetricMax(res, 'LEARNING_LICENSES')),
    adminLicenses: ({ selectors }) =>
      createSelector(selectors.response, res => getMetricValue(res, 'ADMIN_LICENSES')),
    pricePerUserAmount: ({ selectors }) =>
      createSelector(selectors.response, res => res?.plan?.price?.price?.amount ?? 0),
    pricePerUserCurrency: ({ selectors }) =>
      createSelector(selectors.response, res => res?.plan?.price?.price?.currency ?? 'EUR'),
    totalAmount: ({ selectors }) =>
      createSelector(selectors.response, res => res?.usage?.price?.amount ?? 0),
    totalCurrency: ({ selectors }) =>
      createSelector(selectors.response, res => res?.usage?.price?.currency ?? 'EUR'),
    paymentInterval: ({ selectors }) =>
      createSelector(selectors.response, res => res?.payment?.interval),
    startAt: ({ selectors }) => createSelector(selectors.response, res => res?.startAt ?? ''),
    paymentMethod: ({ selectors }) =>
      createSelector(selectors.response, res => res?.payment?.method),
    paymentNextDate: ({ selectors }) =>
      createSelector(selectors.response, res => res?.payment?.nextDate ?? ''),
  },
});
